import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import {
  PieChart,
  Pie,
  Label,
} from "recharts";
import { TenderState } from "@/graphql/__generated__/graphql";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ChartConfig, ChartTooltip, ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";

interface TenderStateStats {
  count: number;
  state: TenderState;
}

interface TenderStateDoughnutChartProps {
  tenderStateStats: TenderStateStats[];
}

const DEFAULT_TENDER_STATE_COLOR = "#CBD5E1";

const TenderStateDoughnutChart: React.FC<TenderStateDoughnutChartProps> = ({
  tenderStateStats,
}: TenderStateDoughnutChartProps): JSX.Element => {
  // Hooks
  const { t } = useTranslation(["components", "common"]);

  const chartConfig = {
    tenders: {
      label: t("common:chances"),
    },
    [TenderState.PendingBidDecision]: {
      label: t("common:tenderstate.pendingbiddecision"),
      color: "#FDE047",
    },
    [TenderState.PendingMarginDecision]: {
      label: t("common:tenderstate.pendingmarginapproval"),
      color: "#FCD34D",
    },
    [TenderState.Editing]: {
      label: t("common:tenderstate.editing"),
      color: "#FDBA74",
    },
    [TenderState.Lost]: {
      label: t("common:tenderstate.lost"),
      color: "#FCA5A5",
    },
    [TenderState.ReadyToSubmit]: {
      label: t("common:tenderstate.readytosubmit"),
      color: "#5EEAD4",
    },
    [TenderState.Won]: {
      label: t("common:tenderstate.won"),
      color: "#86EFAC",
    },
    [TenderState.NotBidding]: {
      label: t("common:tenderstate.notbidding"),
      color: "#D1D5DB",
    },
    [TenderState.Submitted]: {
      label: t("common:tenderstate.submitted"),
      color: "#D8B4FE",
    },
    [TenderState.Annulment]: {
      label: t("common:tenderstate.annulment"),
      color: "#94A3B8",
    },
  } satisfies ChartConfig;

  const chartData = tenderStateStats?.map((stat) => ({
    status: stat.state,
    tenders: stat.count,
    fill: chartConfig[stat.state]?.color ?? DEFAULT_TENDER_STATE_COLOR,
  })) ?? [];

  const totalTenders = chartData.reduce((acc, curr) => acc + curr.tenders, 0);

  return (
    <div className="w-full flex flex-col flex-1 p-2 overflow-hidden">
      <div className="w-full text-center flex items-center">
        <h1 className="font-semibold flex-1">{t("tenderstatechart.title")}</h1>

        <HoverCard>
          <HoverCardTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 p-0 hover:cursor-help"
            >
              <InformationCircleIcon className="h-5 w-5" />
            </Button>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 text-sm font-normal">
            {t("tenderstatechart.tooltip")}
          </HoverCardContent>
        </HoverCard>
      </div>
      {chartData.length === 0 ? (
        <div className="w-full text-center h-full justify-center items-center flex">
          <p>{t("common:nodataavailable")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 overflow-hidden flex-1">
          <ChartContainer
            config={chartConfig}
            className="mx-auto h-[180px] min-h-[180px] max-h-[180px] w-full"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Pie
                data={chartData}
                dataKey="tenders"
                nameKey="status"
                innerRadius={55}
                outerRadius={80}
                strokeWidth={5}
              >
                <Label
                  position="center"
                  value={totalTenders.toLocaleString()}
                  className="fill-foreground text-3xl font-bold -translate-y-2"
                />
                <Label
                  position="center"
                  value={t("common:chances")}
                  className="fill-muted-foreground translate-y-4"
                />
              </Pie>
            </PieChart>
          </ChartContainer>

          {/* Two-column legend: state name | count */}
          <div className="overflow-y-auto flex-1">
            <table className="w-full text-xs">
              <tbody>
                {chartData.map((entry) => (
                  <tr key={entry.status} className="border-b border-slate-100 last:border-0">
                    <td className="py-1 pr-2">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: entry.fill }}
                        />
                        <span className="text-muted-foreground">
                          {chartConfig[entry.status as keyof typeof chartConfig]?.label ?? entry.status}
                        </span>
                      </div>
                    </td>
                    <td className="py-1 text-right font-medium tabular-nums">
                      {entry.tenders.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenderStateDoughnutChart;
